import { useEffect, useRef } from 'react';

const ITEM_H = 40;

function range(n: number) {
  return Array.from({ length: n }, (_, i) => i);
}

function WheelColumn({
  max,
  value,
  onChange,
  label,
}: {
  max: number;
  value: number;
  onChange: (n: number) => void;
  label: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const skip = useRef(false);
  const settle = useRef<number | null>(null);
  const values = range(max + 1);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    skip.current = true;
    el.scrollTop = value * ITEM_H;
    const t = window.setTimeout(() => {
      skip.current = false;
    }, 80);
    return () => window.clearTimeout(t);
  }, [value]);

  const commit = () => {
    const el = ref.current;
    if (!el) return;
    const idx = Math.max(0, Math.min(max, Math.round(el.scrollTop / ITEM_H)));
    el.scrollTo({ top: idx * ITEM_H, behavior: 'smooth' });
    if (idx !== value) onChange(idx);
  };

  return (
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground text-center mb-1">{label}</div>
      <div className="relative h-[120px] rounded-xl bg-muted/40 border border-border overflow-hidden">
        <div className="pointer-events-none absolute inset-x-1 top-1/2 -translate-y-1/2 h-10 rounded-lg bg-primary/15 border border-primary/30 z-[1]" />
        <div
          ref={ref}
          className="h-full overflow-y-auto overscroll-contain snap-y snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ WebkitOverflowScrolling: 'touch' }}
          onScroll={() => {
            if (skip.current) return;
            if (settle.current) window.clearTimeout(settle.current);
            settle.current = window.setTimeout(commit, 90);
          }}
          onTouchEnd={commit}
          onMouseUp={commit}
        >
          <div style={{ height: ITEM_H }} />
          {values.map((n) => (
            <div
              key={n}
              className={`snap-center flex items-center justify-center text-lg font-semibold tabular-nums ${
                n === value ? 'text-primary' : 'text-muted-foreground/70'
              }`}
              style={{ height: ITEM_H }}
            >
              {String(n).padStart(2, '0')}
            </div>
          ))}
          <div style={{ height: ITEM_H }} />
        </div>
      </div>
    </div>
  );
}

/** Scrollable hours / minutes / seconds picker — no keyboard. */
export function DurationWheelPicker({
  minutes,
  onChange,
}: {
  minutes: number;
  onChange: (minutes: number) => void;
}) {
  const totalSec = Math.max(0, Math.round((minutes || 0) * 60));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;

  const set = (nh: number, nm: number, ns: number) => {
    onChange(nh * 60 + nm + ns / 60);
  };

  return (
    <div className="flex gap-2">
      <WheelColumn label="Hrs" max={9} value={Math.min(9, h)} onChange={(v) => set(v, m, s)} />
      <WheelColumn label="Min" max={59} value={m} onChange={(v) => set(h, v, s)} />
      <WheelColumn label="Sec" max={59} value={s} onChange={(v) => set(h, m, v)} />
    </div>
  );
}
